import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';

export type VoidCopyOptions = {
  copyWorksheetForward: boolean;
  copyNotesForward: boolean;
};

export interface VoidClaimsConfirmDialogProps {
  open: boolean;
  selectedCount: number;
  /** True on tabs at/past Pending Payment, where the claim may already be with the payer. */
  postSend: boolean;
  onConfirm: (options: VoidCopyOptions) => void;
  onCancel: () => void;
}

const baseMessage = (count: number) =>
  count === 1
    ? 'This voids 1 billing cycle in Roger and releases its visits for rebilling.'
    : `This voids ${count} billing cycles in Roger and releases their visits for rebilling.`;

const PAYER_WARNING =
  'The payer is not notified — if a claim was already submitted or paid, ' +
  'you must cancel it with the payer separately.';

export const VoidClaimsConfirmDialog: React.FC<
  VoidClaimsConfirmDialogProps
> = ({ open, selectedCount, postSend, onConfirm, onCancel }) => {
  const [copyWorksheetForward, setCopyWorksheetForward] = useState(true);
  const [copyNotesForward, setCopyNotesForward] = useState(true);

  useEffect(() => {
    if (open) {
      setCopyWorksheetForward(true);
      setCopyNotesForward(true);
    }
  }, [open]);

  const message = postSend
    ? `${baseMessage(selectedCount)} ${PAYER_WARNING}`
    : baseMessage(selectedCount);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Void claims?</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-3 py-2'>
          <label className='flex items-center gap-2 text-sm'>
            <Checkbox
              checked={copyWorksheetForward}
              onCheckedChange={checked =>
                setCopyWorksheetForward(checked === true)
              }
              checkboxSize='sm'
            />
            Copy claim worksheet
          </label>
          <label className='flex items-center gap-2 text-sm'>
            <Checkbox
              checked={copyNotesForward}
              onCheckedChange={checked => setCopyNotesForward(checked === true)}
              checkboxSize='sm'
            />
            Copy notes
          </label>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                copyWorksheetForward,
                copyNotesForward,
              })
            }
          >
            Void
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export interface VoidClaimsButtonProps {
  selectedCount: number;
  postSend: boolean;
  disabled: boolean;
  isSubmitting: boolean;
  onConfirm: (options: VoidCopyOptions) => void;
}

export const VoidClaimsButton: React.FC<VoidClaimsButtonProps> = ({
  selectedCount,
  postSend,
  disabled,
  isSubmitting,
  onConfirm,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <VoidClaimsConfirmDialog
        open={confirmOpen}
        selectedCount={selectedCount}
        postSend={postSend}
        onConfirm={options => {
          setConfirmOpen(false);
          onConfirm(options);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <Button
        variant='destructive'
        disabled={disabled}
        onClick={() => setConfirmOpen(true)}
      >
        {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
        Void
      </Button>
    </>
  );
};
